import React from "react"
import {useTranslation} from "react-i18next"

import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"

import {COLORS} from "../../../../../config/constants"
import {formatDemoScenarioTitle} from './helpers'

const DemoHero = ({
  backgroundImage,
  onSelectScenario,
  scenarios,
  selectedScenarioSlug,
}: DemoHeroProps) => {

  const { t, i18n } = useTranslation()

  const selectedScenarioIndex = scenarios.findIndex(({ slug }) => slug === selectedScenarioSlug)
  const selectedScenario = scenarios[selectedScenarioIndex] || scenarios[0]
  const scenarioImage = backgroundImage ?? selectedScenario?.background_image ?? undefined
  const shouldShowImage = Boolean(scenarioImage)

  const scenarioRows = [ scenarios.slice(0, 3), scenarios.slice(3) ]

  return (
    <section className='demo-hero'>
      <div className='demo-hero__inner' data-category={selectedScenario?.category}>
        {shouldShowImage && (
          <div className='demo-hero__illustration' aria-hidden='true'>
            <img className='demo-hero__illustration-image' src={scenarioImage} alt='' />
          </div>
        )}
        <Typography
          className='demo-hero__kicker'
          sx={{
            color: COLORS.WHITE,
            fontFamily: 'Demo Source Code Pro',
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: 1.6,
            letterSpacing: '.2em',
            marginBottom: '32px',
            '@media (min-width: 768px) and (max-width: 1199px)': {
              fontSize: 16,
              marginBottom: '24px'
            },
            '@media (min-width: 320px) and (max-width: 767px)': {
              fontSize: 12,
              marginBottom: '16px'
            },
          }}
        >
          {t('calc__text__demoHeroKicker')}
        </Typography>
        <div className='demo-hero__categories' lang={i18n.resolvedLanguage}>
          {scenarioRows.map((row, rowIndex) => (
            <div key={rowIndex} className='demo-hero__category-row'>
              {row.map(({ button_title, emoji, slug }) => {
                const isActive = slug === selectedScenarioSlug
                const categoryBackground = isActive ? COLORS.WHITE : 'transparent'
                const categoryColor = isActive ? COLORS.BLACK_DARK : COLORS.WHITE

                return (
                  <Button
                    key={slug}
                    type='button'
                    variant='outlined'
                    aria-pressed={isActive}
                    className={isActive ? 'demo-hero__category demo-hero__category_active' : 'demo-hero__category'}
                    data-demo-allowed='hero-category'
                    onClick={() => onSelectScenario(slug)}
                    sx={{
                      padding: '11px 23px',
                      borderRadius: '100px',
                      textTransform: 'none',
                      '@media (min-width: 768px) and (max-width: 1199px)': {
                        padding: '9px 19px'
                      },
                      '@media (min-width: 320px) and (max-width: 767px)': {
                        padding: '7px 15px',
                        minHeight: 0
                      },
                      '&.MuiButton-outlined': {
                        background: categoryBackground,
                        border: `1px solid ${!isActive ? 'rgba(255,255,255,0.56)' : COLORS.WHITE}`,
                        color: categoryColor,
                        '& .MuiTouchRipple-root': {
                          color: COLORS.WHITE,
                        },
                        '&:hover': {
                          background: categoryBackground,
                          borderColor: COLORS.WHITE,
                          color: categoryColor,
                        },
                        '&:active': {
                          background: categoryBackground,
                          borderColor: COLORS.WHITE,
                          color: categoryColor,
                        },
                      },
                    }}
                  >
                    <Typography
                      variant='h6'
                      className='demo-hero__category-title'
                      sx={{
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        fontSize: '18px',
                        fontWeight: 700,
                        lineHeight: 1.5,
                        letterSpacing: 0,
                        '@media (min-width: 768px) and (max-width: 1199px)': {
                          fontSize: 16,
                          gap: '14px'
                        },
                        '@media (min-width: 320px) and (max-width: 767px)': {
                          fontSize: 14,
                          gap: '12px'
                        },
                      }}
                    >
                      <Typography
                        component='span'
                        sx={{
                          fontSize: 24,
                          fontWeight: 400,
                          lineHeight: 1,
                          '@media (min-width: 768px) and (max-width: 1199px)': {
                            fontSize: 20
                          },
                          '@media (min-width: 320px) and (max-width: 767px)': {
                            fontSize: 16,
                            lineHeight: '16px',
                            width: 16,
                            textAlign: 'center'
                          }
                        }}
                        aria-hidden='true'
                        className='demo-hero__category-emoji'
                      >
                        {emoji}
                      </Typography>
                      <Typography
                        component='span'
                        className='demo-hero__category-label'
                        sx={{
                          font: 'inherit',
                          color: 'inherit',
                          letterSpacing: 0
                        }}
                      >
                        {button_title}
                      </Typography>
                    </Typography>
                  </Button>
                )
              })}
            </div>
          ))}
        </div>
        <Typography
          variant='h3'
          component='h1'
          className='demo-hero__title'
          lang={i18n.resolvedLanguage}
          sx={{
            color: COLORS.WHITE,
            fontSize: '48px',
            fontFamily: 'Demo Nexa, sans-serif',
            fontWeight: 950,
            lineHeight: '1.22',
            '@media (min-width: 768px) and (max-width: 1199px)': {
              fontSize: 44
            },
            '@media (min-width: 320px) and (max-width: 767px)': {
              fontSize: 40
            },
          }}
        >
          {formatDemoScenarioTitle(selectedScenario?.title)}
        </Typography>
      </div>
    </section>
  )
}

export default DemoHero
